import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setExtractedData, setValidationErrors, clearValidationErrors } from '../../../store/reducers/index';
import { useLanguage } from '../../../hooks/useLanguage';
import { deepClone } from '../../../utils/dataHelpers';

export const useDeclarationSummary = (jsonData) => {
  const dispatch = useDispatch();
  const extractedData = useSelector((state) => state.selectedDeclaration.data[0].data);

  // Get validation errors from Redux instead of local state
  const validationErrors = useSelector(state => state.validationErrors.errors);
  // const isSpanishFromRedux = useSelector(state => state.validationErrors.isSpanish);

  const [isSpanish] = useLanguage();
  const [editableData, setEditableData] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  
  // Store original data for cancel functionality
  const originalDataRef = useRef(null);

  useEffect(() => {
    const dataToUse = extractedData || jsonData;
    let newData = [];
    if (Array.isArray(dataToUse)) {
      newData = deepClone(dataToUse);
      setEditableData(newData);
    } else if (dataToUse) {
      newData = [deepClone(dataToUse)];
      setEditableData(newData);
    } else {
      setEditableData([]);
    }
    
    // Store original data when first loaded
    if (newData.length > 0 && !originalDataRef.current) {
      originalDataRef.current = deepClone(newData);
    }
  }, [jsonData, extractedData]);

  // No local setIsSpanish or sync with Redux validation errors

  // Automatically validate and log whenever data changes
  useEffect(() => {
    if (editableData && editableData.length > 0) {
      const errors = validateRequiredFields();
      
      // Dispatch errors to Redux so the sidebar can show them
      if (errors.length > 0) {
        dispatch(setValidationErrors(errors, isSpanish));
      } else {
        // Clear validation errors when all fields are valid
        dispatch(clearValidationErrors());
      }
    }
  }, [editableData, isSpanish, dispatch]);

  const handleChange = (declarationIndex, field, value) => {
    const updatedData = [...editableData];
    updatedData[declarationIndex][field] = value;
    setEditableData(updatedData);
    setIsDirty(true);
    
    // Clear validation errors when user starts editing
    if (validationErrors && validationErrors.length > 0) {
      dispatch(clearValidationErrors());
    }
  };

  const validateRequiredFields = () => {
    const errors = [];
  
    editableData.forEach((declaration, declarationIndex) => {
      const declarationErrors = [];
      const itemErrors = [];
      const packageErrors = [];
      const documentErrors = [];
  
      // Check declaration required fields
      if (!declaration.expediente) {
        declarationErrors.push({
          field: `expediente_${declarationIndex + 1}`,
          error: !isSpanish ? "Declaration" : "Expediente"
        });
      }
      if (!declaration.tipo) {
        declarationErrors.push({
          field: `tipo_${declarationIndex + 1}`,
          error: !isSpanish ? "Type" : "Tipo"
        });
      }
      // Clave Declarante is CRITICAL for export declarations
      if (!declaration.clave_declarante) {
        declarationErrors.push({
          field: `clave_declarante_${declarationIndex + 1}`,
          error: !isSpanish ? "Declarant Key" : "Clave Declarante"
        });
      }
      // Phone number of declarant is CRITICAL
      if (!declaration.contacto_telefono) {
        declarationErrors.push({
          field: `contacto_telefono_${declarationIndex + 1}`,
          error: !isSpanish ? "Phone Number" : "Teléfono"
        });
      }
      // Security Data Flag is CRITICAL (must be 0 or 1, not null)
      if (declaration.flag_datos_seguridad === null || declaration.flag_datos_seguridad === undefined) {
        declarationErrors.push({
          field: `flag_datos_seguridad_${declarationIndex + 1}`,
          error: !isSpanish ? "Security Data Flag" : "Datos de Seguridad"
        });
      }
  
      // Check items required fields
      declaration.partidas?.forEach((item, itemIndex) => {
        const currentItemErrors = [];
  
        if (!item.descripcion_mercancia) {
          currentItemErrors.push({
            field: `descripcion_mercancia_${declarationIndex + 1}_${itemIndex}`,
            error: !isSpanish ? "Goods Description" : "Descripción de Mercancía"
          });
        }
        if (!item.codigo_nc) {
          currentItemErrors.push({
            field: `codigo_nc_${declarationIndex + 1}_${itemIndex}`,
            error: !isSpanish ? "NC Code" : "Código NC"
          });
        }
        if (!item.masa_bruta) {
          currentItemErrors.push({
            field: `masa_bruta_${declarationIndex + 1}_${itemIndex}`,
            error: !isSpanish ? "Gross Weight" : "Masa Bruta"
          });
        }
        if (!item.masa_neta) {
          currentItemErrors.push({
            field: `masa_neta_${declarationIndex + 1}_${itemIndex}`,
            error: !isSpanish ? "Net Weight" : "Masa Neta"
          });
        }
        if (!item.pais_origen) {
          currentItemErrors.push({
            field: `pais_origen_${declarationIndex + 1}_${itemIndex}`,
            error: !isSpanish ? "Country of Origin" : "País de Origen"
          });
        }

        if (currentItemErrors.length > 0) {
          itemErrors.push({ 
            title: !isSpanish ? `Item #${itemIndex + 1}` : `Partida #${itemIndex + 1}`, 
            errors: currentItemErrors 
          });
        }
  
        // Check packages required fields - organized by package
        item.embalajes?.forEach((embalaje, embalajeIndex) => {
          const currentPackageErrors = [];
          
          if (!embalaje.tipo) {
            currentPackageErrors.push({
              field: `packages_tipo_${declarationIndex}_${itemIndex}_${embalajeIndex}`,
              error: !isSpanish ? "Type" : "Tipo"
            });
          }
          if (!embalaje.es_interno_o_externo) {
            currentPackageErrors.push({
              field: `packages_es_interno_o_externo_${declarationIndex}_${itemIndex}_${embalajeIndex}`,
              error: !isSpanish ? "Internal or External" : "Es Interno o Externo"
            });
          }

          if (!embalaje.cantidad) {
            currentPackageErrors.push({
              field: `packages_cantidad_${declarationIndex}_${itemIndex}_${embalajeIndex}`,
              error: !isSpanish ? "Quantity" : "Cantidad"
            });
          }
          
          embalaje.marcas?.forEach((marca, marcaIndex) => {
            if (!marca.marca) {
              currentPackageErrors.push({
                field: `packages_marcas_marca_${declarationIndex}_${itemIndex}_${embalajeIndex}_${marcaIndex}`,
                error: !isSpanish ? `Mark #${marcaIndex + 1}` : `Marca #${marcaIndex + 1}`
              });
            }
          });

          // Add package errors if any exist
          if (currentPackageErrors.length > 0) {
            packageErrors.push({
              itemTitle: !isSpanish ? `Item #${itemIndex + 1}` : `Partida #${itemIndex + 1}`,
              packageTitle: !isSpanish ? `Package #${embalajeIndex + 1}` : `Embalaje #${embalajeIndex + 1}`,
              errors: currentPackageErrors
            });
          }
        });
  
        // Check documents required fields - organized by document
        item.documentos?.forEach((documento, docIndex) => {
          const currentDocumentErrors = [];
          
          if (!documento.codigo_documento) {
            currentDocumentErrors.push({
              field: `documents_codigo_documento_${declarationIndex}_${itemIndex}_${docIndex}`,
              error: !isSpanish ? "Document Code" : "Código de Documento"
            });
          }
          if (!documento.tipo) {
            currentDocumentErrors.push({
              field: `documents_tipo_${declarationIndex}_${itemIndex}_${docIndex}`,
              error: !isSpanish ? "Type" : "Tipo"
            });
          }

          // Add document errors if any exist
          if (currentDocumentErrors.length > 0) {
            documentErrors.push({
              itemTitle: !isSpanish ? `Item #${itemIndex + 1}` : `Partida #${itemIndex + 1}`,
              documentTitle: !isSpanish ? `Document #${docIndex + 1}` : `Documento #${docIndex + 1}`,
              errors: currentDocumentErrors
            });
          }
        });
      });
  
      // Add all errors for this declaration
      if (declarationErrors.length > 0 || itemErrors.length > 0 || packageErrors.length > 0 || documentErrors.length > 0) {
        errors.push({
          declarationNumber: declarationIndex + 1,
          declarationErrors,
          itemErrors,
          packageErrors,
          documentErrors
        });
      }
    });
  
    return errors;
  };

  const saveChanges = () => {
    const errors = validateRequiredFields();
    
    if (errors.length > 0) {
      dispatch(setValidationErrors(errors, isSpanish));
      return { success: false, errors };
    }
    dispatch(clearValidationErrors());
    dispatch(setExtractedData(editableData));
    setIsDirty(false);
    // Update original data ref to the saved state so cancel will reset to this point
    originalDataRef.current = deepClone(editableData);
    return { success: true };
  };

  const cancelChanges = () => {
    // Reset to original data that was stored when first loaded
    if (originalDataRef.current) {
      setEditableData(deepClone(originalDataRef.current));
      setIsDirty(false);
      // Clear validation errors when canceling
      dispatch(clearValidationErrors());
    } else {
      // Fallback to extractedData or jsonData if originalDataRef is not set
      if (extractedData) {
        setEditableData(deepClone(extractedData));
      } else if (jsonData) {
        setEditableData(deepClone(jsonData));
      } else {
        setEditableData([]);
      }
      setIsDirty(false);
      dispatch(clearValidationErrors());
    }
  };

  const countTotalItems = () => {
    if (Array.isArray(editableData)) {
      return editableData.reduce((total, declaration) => {
        return total + (declaration.partidas?.length || 0);
      }, 0);
    }
    return 0;
  };

  return {
    isSpanish,
    editableData,
    setEditableData,
    isDirty,
    setIsDirty,
    validationErrors, // This now comes from Redux
    handleChange,
    saveChanges,
    cancelChanges,
    countTotalItems
  };
};